import type { ComponentType } from 'react'
import type { SvgIconProps } from '@mui/material/SvgIcon'
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded'
import CelebrationRoundedIcon from '@mui/icons-material/CelebrationRounded'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded'
import ThumbUpRoundedIcon from '@mui/icons-material/ThumbUpRounded'
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded'
import type { ChecklistComment } from '../../types/schema'
import { useNls } from '../../nls/nlsProvider'
import type { NlsKey } from '../../nls/strings_nls'
import { getChecklistCommentReactionLabel } from '../ChecklistBoard/ChecklistNls'
import { StatusChip } from '../Common'
import { Button } from '../Common/Button'
import { Pressable } from '../Common/Pressable'

const COMMENT_REACTIONS = ['👍', '❤️', '🎉', '👀', '✅'] as const

type ReactionMeta = { Icon: ComponentType<SvgIconProps>; labelKey: NlsKey }
const REACTION_META: Record<(typeof COMMENT_REACTIONS)[number], ReactionMeta> = {
  '👍': { Icon: ThumbUpRoundedIcon, labelKey: 'NLSChecklistCommentReactionThumbsUp' },
  '❤️': { Icon: FavoriteRoundedIcon, labelKey: 'NLSChecklistCommentReactionHeart' },
  '🎉': { Icon: CelebrationRoundedIcon, labelKey: 'NLSChecklistCommentReactionCelebrate' },
  '👀': { Icon: VisibilityRoundedIcon, labelKey: 'NLSChecklistCommentReactionEyes' },
  '✅': { Icon: CheckCircleRoundedIcon, labelKey: 'NLSChecklistCommentReactionCheck' },
}

export const getCommentAuthorInitials = (name: string): string => {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('')
  return initials || 'U'
}

type NodeDetailCommentCardProps = {
  comment: ChecklistComment
  timestampLabel: string
  currentUserId?: string | null
  canReply: boolean
  canEdit: boolean
  canDelete: boolean
  canReact: boolean
  onReply: () => void
  onEdit: () => void
  onDelete: () => void
  onToggleReaction: (emoji: string) => void
}

export const NodeDetailCommentCard = ({
  comment,
  timestampLabel,
  currentUserId,
  canReply,
  canEdit,
  canDelete,
  canReact,
  onReply,
  onEdit,
  onDelete,
  onToggleReaction,
}: NodeDetailCommentCardProps) => {
  const { t } = useNls()
  const isDeleted = Boolean(comment.deletedAt)

  return (
    <article className={`nodeCommentItem ${comment.parentCommentId ? 'isReply' : ''} ${isDeleted ? 'isDeleted' : ''}`}>
      <header className="nodeCommentItemHeader">
        <span className="nodeCommentAvatar" aria-hidden="true">
          {getCommentAuthorInitials(comment.userName)}
        </span>
        <div className="nodeCommentHeaderCopy">
          <div className="nodeCommentIdentityRow">
            <strong>{comment.userName}</strong>
            {comment.parentCommentId && (
              <StatusChip size="sm" tone="muted" className="nodeCommentMetaChip">
                {t('NLSChecklistCommentMetaReply')}
              </StatusChip>
            )}
            {comment.editedAt && !isDeleted && (
              <StatusChip size="sm" tone="muted" className="nodeCommentMetaChip">
                {t('NLSChecklistCommentMetaEdited')}
              </StatusChip>
            )}
          </div>
          <time>{timestampLabel}</time>
        </div>
      </header>

      <p className="nodeCommentBody">{isDeleted ? t('NLSChecklistCommentDeleted') : comment.text}</p>

      {!isDeleted && (comment.attachments?.length ?? 0) > 0 && (
        <div className="nodeCommentAttachments">
          {comment.attachments?.map((attachment) => (
            <a key={attachment.id} href={attachment.downloadUrl} target="_blank" rel="noreferrer">
              <AttachFileRoundedIcon fontSize="inherit" />
              <span>{attachment.name}</span>
            </a>
          ))}
        </div>
      )}

      {!isDeleted && (
        <footer className="nodeCommentFooter">
          <div className="nodeCommentReactions">
            {COMMENT_REACTIONS.map((emoji) => {
              const count = comment.reactions?.[emoji]?.length ?? 0
              const active = comment.reactions?.[emoji]?.includes(currentUserId ?? '')
              const { Icon, labelKey } = REACTION_META[emoji]
              const label = t(labelKey)
              return (
                <Pressable
                  key={emoji}
                  className={`nodeCommentReactionButton ${active ? 'isActive' : ''}`}
                  disabled={!canReact}
                  aria-pressed={active}
                  aria-label={getChecklistCommentReactionLabel(t, label, count)}
                  onClick={() => onToggleReaction(emoji)}
                >
                  <span className="nodeCommentReactionGlyph" aria-hidden="true"><Icon fontSize="inherit" /></span>
                  {count > 0 && <span className="nodeCommentReactionCount">{count}</span>}
                </Pressable>
              )
            })}
          </div>

          <div className="nodeCommentActions">
            <Button variant="tertiary" size="sm" disabled={!canReply} onClick={onReply}>{t('NLSChecklistCommentActionReply')}</Button>
            {canEdit && <Button variant="tertiary" size="sm" onClick={onEdit}>{t('NLSChecklistCommentActionEdit')}</Button>}
            {canDelete && <Button variant="tertiary" size="sm" tone="danger" onClick={onDelete}>{t('NLSChecklistCommentActionDelete')}</Button>}
          </div>
        </footer>
      )}
    </article>
  )
}
